import { useAction, useAtom } from '@reatom/npm-react'
import { useCallback, useEffect, useRef } from 'react'
import { match } from 'ts-pattern'

import { AsyncStateN } from '@blade/std-lib'
import {
  otherUserDelugeIdAtom,
  refreshUserProfileAction,
  userProfileAtom,
  userProfileStateAtom,
} from '@root/entities/profile'
import { ProfileOwner } from '@root/features/user/profile/model/profile-owner'
import { InfoMessage } from '@root/shared/ui-kit'

import { ProfilePositionsView } from './profile-positions-view'
import { ProfileReferral } from './profile-referral'
import { ProfileScreenSkeleton } from './profile-screen-skeleton'
import { ProfileStatistics } from './profile-statistics'
import { ProfileTransactions } from './profile-transactions'

import type { DelugeId } from '@root/entities/user-short'
import type { ProfileTab } from '@root/features/user/profile-header/atoms'

type ProfileScreenOtherProps = {
  delugeId: DelugeId
  tab: ProfileTab
}

export const ProfileScreenOther: React.FC<ProfileScreenOtherProps> = ({
  delugeId,
  tab,
}) => {
  const [profile] = useAtom(userProfileAtom)
  const [, setOtherUserDelugeId] = useAtom(otherUserDelugeIdAtom)

  const [profileState = AsyncStateN.notInitiated()] =
    useAtom(userProfileStateAtom)
  const refresh = useAction(refreshUserProfileAction)
  const withPreloader = useRef(true)

  useEffect(() => {
    setOtherUserDelugeId(delugeId)

    return () => {
      setOtherUserDelugeId(undefined)
    }
  }, [delugeId, setOtherUserDelugeId])

  useEffect(() => {
    if (AsyncStateN.isSuccess(profileState)) {
      withPreloader.current = false
    }
  }, [profileState])

  const handleRetry = useCallback(() => {
    refresh(delugeId)
  }, [delugeId, refresh])

  useEffect(() => {
    handleRetry()
  }, [handleRetry])

  return AsyncStateN.matchWaiting(profileState, {
    Waiting: () =>
      withPreloader.current ? <ProfileScreenSkeleton /> : undefined,
    Error: () => <InfoMessage intent="error" onRetry={handleRetry} />,
    Success: () => {
      return (
        match(tab)
          .with('referral', () => <ProfileReferral />)
          .with('transactions', () => (
            <ProfileTransactions profileOwner={ProfileOwner.AnotherUsers} />
          ))
          .with('stats', () =>
            profile ? (
              <ProfileStatistics
                delugeId={profile.delugeId}
                wallets={profile.wallets}
                profileOwner={ProfileOwner.AnotherUsers}
              />
            ) : undefined,
          )
          // we do not support premium for other users
          // .with('premium', () => (
          //   <PremiumRoot>
          //     <ProfilePremiumList />
          //   </PremiumRoot>
          // ))
          .with('positions-active', () =>
            profile?.delugeId ? (
              <ProfilePositionsView delugeId={profile.delugeId} />
            ) : undefined,
          )
          .run()
      )
    },
  })
}
